import { useBundleTagline } from "@/lib/i18n/content";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, Loader2, Sparkles, CreditCard, ShieldCheck, Truck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PaymentTrustBadges } from "@/components/PaymentTrustBadges";
import { SOLD_OUT, PREPAID_ONLY, isComingSoon } from "@/lib/storeStatus";
import { useCartStore } from "@/stores/cartStore";
import { NO_COST_EMI_MIN_INR } from "@/lib/checkout-pricing";
import { getPDPContentForHandle } from "@/lib/productContent";
import { productIdForHandle } from "@/lib/productIds";
import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import type { BundlePageData } from "@/lib/bundlePage";
import { bundleGalleryFor } from "@/lib/bundleGallery";
import type { ShopifyProduct } from "@/lib/shopify";
import heroMag__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
import heroOva__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
import heroLiver__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
import { OvaBundleOfferPage } from "./OvaBundleOfferPage";
import { getOvaBundleKind } from "./ovaBundleContent";

const HERO_BY_HANDLE: Record<string, string> = {
  "mag-absorb-pro": heroMag__asset.url,
  "ova-balance": heroOva__asset.url,
  "liver-biome-plus": heroLiver__asset.url,
};

export function bundleHeroFor(p: { handle: string; images: ShopifyProduct["node"]["images"] }): string | null {
  return HERO_BY_HANDLE[p.handle] ?? p.images.edges[0]?.node.url ?? null;
}

const inr = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

export function BundleOfferPage({ data }: { data: BundlePageData }) {
  const kind = getOvaBundleKind(data.items.map((item) => item.handle));
  const validQuantity = Number.isSafeInteger(data.qty) && data.qty >= 1 && data.qty <= Number.MAX_SAFE_INTEGER / 60;
  if (kind && validQuantity && getOvaBundleKind(data.rule.matchHandles) === kind) {
    return <OvaBundleOfferPage data={data} kind={kind} heroFor={bundleHeroFor} />;
  }
  return <LegacyBundleOfferPage data={data} />;
}

function LegacyBundleOfferPage({ data }: { data: BundlePageData }) {
  const { rule, items, qty, subtotal, total, savings, percent } = data;
  const addItems = useCartStore((s) => s.addItems);
  const isLoading = useCartStore((s) => s.isLoading);
  const [added, setAdded] = useState(false);
  const bundleTagline = useBundleTagline();
  const gallery = bundleGalleryFor(rule.slug);
  // A bundle can never be more available than its least available member.
  const comingSoon = items.some((p) => isComingSoon(p.handle));

  async function handleAdd() {
    const lines = items
      .map((p) => {
        const v = p.variants.edges[0]?.node;
        if (!v) return null;
        return {
          variantId: v.id,
          productHandle: p.handle,
          productTitle: p.title,
          imageUrl: p.images.edges[0]?.node.url ?? null,
          price: v.price,
          quantity: qty,
        };
      })
      .filter((l): l is NonNullable<typeof l> => l !== null);
    if (lines.length === 0) return;
    await addItems(lines);
    setAdded(true);
    (window as unknown as { __openCart?: () => void }).__openCart?.();
    setTimeout(() => setAdded(false), 2200);
  }

  return (
    <main className="mx-auto max-w-6xl px-4 pb-20 pt-8 md:px-8 md:pt-14">
      <nav aria-label="Breadcrumb" className="mb-6 text-[11px] text-muted-foreground">
        <Link to="/" className="hover:text-brand">Home</Link>
        <span className="mx-1.5 opacity-50">/</span>
        <Link to="/shop" className="hover:text-brand">Shop</Link>
        <span className="mx-1.5 opacity-50">/</span>
        <span className="text-foreground/70">{rule.title}</span>
      </nav>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:items-start">
        {/* Hero */}
        <div className="min-w-0">
          {rule.goal && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-brand">
              <Sparkles className="h-3 w-3" /> {rule.goal}
            </span>
          )}
          <h1 className="mt-3 min-w-0 max-w-full py-[0.08em] font-display text-3xl font-extrabold leading-[1.2] [overflow-wrap:anywhere] text-brand-deep md:text-5xl">
            {rule.title}
          </h1>
          {rule.tagline && (
            <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">{bundleTagline(rule.slug, rule.tagline)}</p>
          )}

          <div className={`mt-6 grid gap-3 ${items.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
            {items.map((p) => {
              const src = bundleHeroFor(p);
              return (
                <Link
                  key={p.id}
                  to="/products/$handle"
                  params={{ handle: p.handle }}
                  className="group min-w-0 overflow-hidden rounded-2xl border border-border bg-white transition hover:border-brand/30 hover:shadow-luxury"
                >
                  <div className="aspect-square overflow-hidden bg-white">
                    {src && (
                      <img
                        src={src}
                        alt={p.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
                        loading="eager"
                        decoding="async"
                      />
                    )}
                  </div>
                  <p className="px-2.5 py-2 text-center text-[11px] font-semibold leading-tight text-brand-deep md:text-xs">
                    {p.title}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Price + CTA */}
        <aside className="min-w-0 rounded-3xl border border-brand/25 bg-card p-6 shadow-luxury md:sticky md:top-24 md:p-7">
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            {items.map((p) => (
              <li key={p.id} className="flex justify-between gap-3">
                <span className="truncate">
                  {p.title}
                  {qty > 1 ? ` × ${qty}` : ""}
                </span>
                <span className="tabular-nums">
                  {inr(parseFloat(p.variants.edges[0]?.node.price.amount ?? "0") * qty)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-4 border-t border-border pt-4">
            {savings > 0 && (
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Buy separately
                </span>
                <span className="text-sm text-muted-foreground line-through tabular-nums">
                  {inr(subtotal)}
                </span>
              </div>
            )}
            <div className="mt-1 flex items-baseline justify-between">
              <span className="text-[11px] uppercase tracking-[0.16em] text-brand-deep/70">
                Bundle price
              </span>
              <span className="font-display text-3xl font-bold text-brand-deep tabular-nums">
                {inr(total)}
              </span>
            </div>
            {savings > 0 && (
              <p className="mt-2 inline-flex rounded-full bg-brand/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-brand-deep">
                Save {inr(savings)} ({percent}%)
              </p>
            )}
          </div>

          <Button
            data-testid="bundle-add-to-cart"
            size="lg"
            className="cta-glow mt-5 h-12 w-full rounded-full text-sm font-semibold"
            disabled={isLoading || SOLD_OUT || comingSoon}
            onClick={handleAdd}
          >
            {SOLD_OUT ? (
              "Sold out"
            ) : comingSoon ? (
              "Coming soon"
            ) : isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : added ? (
              <>
                <Check className="mr-1.5 h-4 w-4" /> Added to cart
              </>
            ) : (
              "Add bundle to cart"
            )}
          </Button>

          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            {comingSoon
              ? "One product in this bundle is not available to buy yet."
              : PREPAID_ONLY
                ? "The discount applies automatically in your cart. Prepaid orders only."
                : "The discount applies automatically in your cart."}
          </p>

          {total >= NO_COST_EMI_MIN_INR && (
            <p className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-brand-soft/70 px-3 py-1.5 text-[11px] font-semibold text-brand-deep ring-1 ring-brand/20">
              <CreditCard className="h-3 w-3" /> No-cost EMI available on this bundle
            </p>
          )}

          <PaymentTrustBadges />

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Truck className="h-3 w-3 text-brand" aria-hidden /> Free shipping across India
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-brand" aria-hidden /> FSSAI + GMP certified
            </span>
          </div>
        </aside>
      </div>

      {/* Bundle visuals */}
      {gallery.length > 0 && (
        <section className="mt-14" aria-label={`${rule.title} in your daily routine`}>
          <div className="grid gap-4 md:grid-cols-2">
            {gallery.map((img) => (
              <figure
                key={img.url}
                className="overflow-hidden rounded-3xl border border-border bg-white shadow-luxury/40"
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="h-auto w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* What's inside */}
      <section className="mt-14" aria-labelledby="bundle-inside">
        <h2 id="bundle-inside" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          What&apos;s inside
        </h2>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {items.map((p) => {
            const pdp = getPDPContentForHandle(p.handle);
            return (
              <article key={p.id} className="flex flex-col rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-semibold text-brand-deep">{p.title}</h3>
                <p className="mt-2 flex-1 text-xs leading-relaxed text-muted-foreground">
                  {pdp?.tldr.benefit ?? p.description?.slice(0, 180)}
                </p>
                <Link
                  to="/products/$handle"
                  params={{ handle: p.handle }}
                  className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  View product <ArrowRight className="h-3 w-3" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      {/* Reviews for each product in the bundle */}
      {items.some((p) => productIdForHandle(p.handle)) && (
        <section className="mt-14" aria-labelledby="bundle-reviews">
          <h2
            id="bundle-reviews"
            className="font-display text-2xl font-bold text-brand-deep md:text-3xl"
          >
            What customers say
          </h2>
          <div className="mt-5 space-y-10">
            {items.map((p) => {
              const rid = productIdForHandle(p.handle);
              if (!rid) return null;
              return (
                <div key={`reviews-${p.id}`}>
                  <h3 className="font-display text-base font-semibold text-brand-deep">
                    {p.title}
                  </h3>
                  <div className="mt-3">
                    <ReviewsSection productId={rid} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}



      {/* FAQ */}
      <section className="mt-14" aria-labelledby="bundle-faq">
        <h2 id="bundle-faq" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          Bundle FAQs
        </h2>
        <Accordion type="single" collapsible className="mt-4">
          {bundleFaqs(rule.title, percent).map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`}>
              <AccordionTrigger className="text-left text-sm font-semibold text-brand-deep">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </main>
  );
}

export function bundleFaqs(title: string, percent: number): Array<{ q: string; a: string }> {
  return [
    {
      q: "How does the bundle discount work?",
      a: `Adding the ${title} puts each product in your cart as a normal item and applies the ${percent}% bundle saving automatically. The discounted total is what you pay at checkout.${PREPAID_ONLY ? " Orders are prepaid only during pre-booking." : " That includes COD and EMI."}`,
    },
    {
      q: "Can I take these products together?",
      a: "Yes. They are formulated to be complementary and can be taken on the same day. Follow each label's timing guidance, and speak to your doctor first if you are pregnant, nursing, or on prescription medication.",
    },
    {
      q: "Do I get the same guarantee as buying separately?",
      a: "Yes. Bundles ship as individual sealed products and are covered by the same shipping, return and refund policies as single purchases.",
    },
    {
      q: "Can I remove one product from the bundle?",
      a: "You can, but the bundle discount only applies while all the products in this protocol are in your cart.",
    },
  ];
}

